import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
	renderChildrenOrChildrenFunction,
	type IContextChildren,
	type IContextRef,
} from '@netx/core-next';
import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {splitEventHandler} from '@netx/core-next/utils';
import {Snackbar, type SnackbarProps} from './Snackbar';

export type SnackbarServiceContextType = (
	options: Omit<SnackbarProps, 'open'>,
) => void;

export type SnackbarServiceProviderRef =
	IContextRef<SnackbarServiceContextType>;

export type SnackbarServiceProviderProps = {
	ref?: SnackbarServiceProviderRef;
	children: IContextChildren<SnackbarServiceContextType>;
};

// provide default so the world can go on if the provider is not used
const SnackbarServiceContext = createContext<(options: SnackbarProps) => void>(
	() => {},
);

export const useSnackbar = () => useContext(SnackbarServiceContext);

const SnackbarServiceProviderInner = (
	{children}: SnackbarServiceProviderProps,
	ref: SnackbarServiceProviderRef,
) => {
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarState, setSnackbarState] = useState<SnackbarProps>();
	const nextSnackbarStateRef = useRef<SnackbarProps>();
	const lastSnackbarStateRef = useRef<SnackbarProps>();

	useEffect(() => {
		lastSnackbarStateRef.current = snackbarState;
	}, [snackbarState]);

	const handleExited = useCallback(() => {
		setSnackbarState(nextSnackbarStateRef.current || undefined);
		setSnackbarOpen(nextSnackbarStateRef.current != null);
		nextSnackbarStateRef.current = undefined;
	}, []);

	const handleOpenSnackbar = useCallback((options: SnackbarProps) => {
		if (lastSnackbarStateRef.current != null) {
			nextSnackbarStateRef.current = options;
			setSnackbarOpen(false);
			return;
		}
		setSnackbarOpen(true);
		setSnackbarState(options);
	}, []);

	// to support calls from backbone
	useDispatcher('app:snackbar', (options: SnackbarProps) => {
		handleOpenSnackbar(options);
	});

	useImperativeHandle(ref, () => handleOpenSnackbar, [handleOpenSnackbar]);

	return (
		<SnackbarServiceContext.Provider value={handleOpenSnackbar}>
			{renderChildrenOrChildrenFunction(children, handleOpenSnackbar)}

			{ReactDOM.createPortal(
				<Snackbar
					preventCloseOnClickAway
					{...snackbarState}
					open={snackbarOpen}
					onClose={splitEventHandler(snackbarState?.onClose, () => {
						setSnackbarOpen(false);
					})}
					TransitionProps={{
						...snackbarState?.TransitionProps,
						onExited: splitEventHandler(
							snackbarState?.TransitionProps?.onExited,
							handleExited,
						),
					}}
				/>,
				document.body,
			)}
		</SnackbarServiceContext.Provider>
	);
};

export const SnackbarServiceProviderPrimitive = React.forwardRef<
	SnackbarServiceContextType,
	SnackbarServiceProviderProps
>(SnackbarServiceProviderInner);

/**
 * Snackbar service provider
 * @example
 * <SnackbarServiceProvider ref={snackbarRef}>
 * 	...
 * </SnackbarServiceProvider>
 * @example
 * <SnackbarServiceProvider ref={snackbarRef}>
 * 	{(snackbar) => (
 * 		...
 * 	)}
 * </SnackbarServiceProvider>
 */
export const SnackbarServiceProvider = SnackbarServiceProviderPrimitive;

export default SnackbarServiceProvider;
